import { useCallback, useEffect, useRef } from 'react';
import { classifyPaste, snapshotFromEvent } from './paste/classifyPaste';
import { TABLE_EDITOR_SELECTOR } from './test-table-editor/parseClipboardTable';
import type { PasteRoute } from './paste/classifyPaste';

type UseReportPasteProps = {
    disabled: boolean;
    canInsertImage: boolean;
    /** Applies a classified route. Must not touch the event — the context-menu
     *  paste calls the same function with no event at all. */
    onRoute: (route: PasteRoute) => void;
};

/**
 * Whether the paste belongs to something with its own handler.
 *
 * These are focus predicates, not `defaultPrevented` checks, and that is
 * load-bearing: `useTablePaste` is also a bubble-phase document listener, so
 * the two fire in mount order and neither can rely on running second. Keeping
 * their activation conditions disjoint makes the order irrelevant.
 */
const belongsToAnotherHandler = () => {
    // An open dialog owns the paste, whatever is focused inside it. This one is
    // not a focus predicate on purpose: `PasteInstrumentModal` waits for a
    // paste with nothing text-like focused, and without this the canvas would
    // route that paste too — the report would grow a block behind the modal
    // *and* the modal would show the same grid.
    if (document.querySelector('[role="dialog"][data-state="open"]')) return true;

    const active = document.activeElement as HTMLElement | null;
    if (!active) return false;

    // A focused editor/input pastes through the browser (and, for Tiptap,
    // through ProseMirror's own transformPastedHTML).
    if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable) {
        return true;
    }

    // Inside a table editor the paste writes into cells — useTablePaste owns it.
    return active.closest(TABLE_EDITOR_SELECTOR) !== null;
};

/**
 * The report canvas's paste router: the single place external clipboard content
 * becomes a block.
 *
 * Mounted once, above the pages — the tests section renders once per page it
 * spans, so a per-section listener would fire N times for one paste.
 *
 * Runs in the bubble phase and bails on `defaultPrevented`, leaving the
 * capture-phase block-clipboard handler (`useBlockClipboardEvents`) first claim
 * on LaudoPsi's own payloads.
 */
export const useReportPaste = ({ disabled, canInsertImage, onRoute }: UseReportPasteProps) => {
    const stateRef = useRef({ canInsertImage, onRoute });
    stateRef.current = { canInsertImage, onRoute };

    const handlePaste = useCallback((event: ClipboardEvent) => {
        if (event.defaultPrevented) return;
        if (belongsToAnotherHandler()) return;

        const route = classifyPaste(snapshotFromEvent(event), {
            canInsertImage: stateRef.current.canInsertImage,
        });

        // Nothing we can turn into a block: leave the event alone so anything
        // else listening still gets its chance.
        if (route.kind === 'none') return;

        event.preventDefault();
        stateRef.current.onRoute(route);
    }, []);

    useEffect(() => {
        if (disabled) return;

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [disabled, handlePaste]);
};
